import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowRightIcon, TriangleAlertIcon, UserPlusIcon } from 'lucide-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Descope, useDescope, useSession, useUser } from '@descope/react-sdk'
import { EntryShell, GlobalLoading, LoadingInline, track } from '@nxt-platform/global-components'
import { Alert, AlertDescription, AlertTitle } from '@nxt-shared/ui/components/Alert'
import { Badge } from '@nxt-shared/ui/components/Badge'
import { Button } from '@nxt-shared/ui/components/Button'
import { Card } from '@nxt-shared/ui/components/Card'
import { ErrorAlert } from '@nxt-shared/ui/components/ErrorAlert'
import { LinkButton } from '@nxt-shared/ui/components/LinkButton'
import { baseLanguage, useLanguage } from '@nxt-shared/i18n'
import {
  platformManagementAcceptInvitationMutation,
  platformManagementInvitationMetaOptions,
} from '../api/@tanstack/react-query.gen.ts'
import { usePageTitle } from '../features/page-title/pageTitle'
import { me } from '../i18n.ts'

// Invitation landing (rework-invite-flow). Deliberately OUTSIDE the /me layout (me_. prefix): the
// visitor typically has no session yet — this page renders the invitation summary first and embeds
// the existing sign-up-or-in flow instead of the layout's generic login gate.
export const Route = createFileRoute('/me_/invite')({
  validateSearch: (search: Record<string, unknown>): { token: string } => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: InviteLandingRoute,
})

const titleClassName = 'text-2xl font-bold'

function InvalidCard({ title, description }: { title: string; description: string }) {
  return (
    <Card className="p-6 space-y-2">
      <h1 className={titleClassName}>{title}</h1>
      <p>{description}</p>
    </Card>
  )
}

function InviteLandingRoute() {
  const t = me.useT()
  const { token } = Route.useSearch()
  usePageTitle(t('pageTitle.invitation'))

  const meta = useQuery({
    ...platformManagementInvitationMetaOptions({ query: { token } }),
    enabled: token.length > 0,
    staleTime: 30_000,
    retry: 1,
  })

  if (!token) {
    return (
      <EntryShell>
        <InvalidCard
          title={t('invite.missingTokenTitle')}
          description={t('invite.missingTokenBody')}
        />
      </EntryShell>
    )
  }

  if (meta.isPending) return <GlobalLoading />

  if (meta.isError || !meta.data) {
    return (
      <EntryShell>
        <ErrorAlert
          title={t('invite.loadFailedTitle')}
          description={t('invite.loadFailedBody')}
          onRetry={() => void meta.refetch()}
          isRetrying={meta.isFetching}
        />
      </EntryShell>
    )
  }

  const info = meta.data

  if (info.state === 'invalid') {
    return (
      <EntryShell>
        <InvalidCard
          title={t('invite.invalidTitle')}
          description={t('invite.invalidBody')}
        />
      </EntryShell>
    )
  }

  if (info.state === 'expired') {
    return (
      <EntryShell>
        <InvalidCard
          title={t('invite.expiredTitle')}
          description={t('invite.expiredBody')}
        />
      </EntryShell>
    )
  }

  if (info.state === 'accepted') {
    return (
      <EntryShell>
        <Card className="p-6 space-y-4">
          <h1 className={titleClassName}>{t('invite.acceptedTitle')}</h1>
          <p>{t('invite.acceptedBody')}</p>
          {/* A full page load, so the session is refreshed with the access the invitation granted. */}
          <LinkButton icon={ArrowRightIcon} to="/me" reloadDocument>{t('invite.goToOrganizations')}</LinkButton>
        </Card>
      </EntryShell>
    )
  }

  return <PendingInvite token={token} info={info} />
}

function PendingInvite({
  token,
  info,
}: {
  token: string
  info: {
    descopeId?: string
    tenantName?: string | null
    organizationLabels?: string[]
    email?: string
    displayName?: string
  }
}) {
  const t = me.useT()
  const language = useLanguage()
  const { isAuthenticated, isSessionLoading } = useSession()
  const tenantName = info.tenantName ?? null

  return (
    <EntryShell>
      <Card className="p-6 space-y-3">
        <h1 className={titleClassName}>
          {tenantName !== null ? t('invite.invitedTo', { tenant: tenantName }) : t('invite.invitedToSomeTenant')}
        </h1>
        {(info.organizationLabels?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-2">
            {info.organizationLabels!.map((label) => (
              <Badge key={label} variant="secondary">
                {label}
              </Badge>
            ))}
          </div>
        )}
        {info.email && (
          <p className="text-sm text-muted-foreground">
            <me.Trans k="invite.sentTo" values={{ email: info.email }} components={{ b: <span className="font-medium" /> }} />
          </p>
        )}
      </Card>

      {isSessionLoading ? (
        <LoadingInline />
      ) : isAuthenticated ? (
        <AcceptCard token={token} invitedEmail={info.email} tenantName={tenantName} />
      ) : (
        <Card className="p-6 space-y-3">
          <p>{t('invite.signInToAccept')}</p>
          {/* Deliberately NO tenant pre-selection: the visitor is not a member of the inviting
              tenant until they accept, and the flow's sign-up branch rejects a selected tenant for
              non-members (Descope E061007 "Not authorized for selected tenant", staging QA defect 1).
              Sign-up therefore runs under project-level auth policy; the tenant's own settings apply
              from their first tenant-scoped login onward. The invited address and display name
              prefill the flow's inputs (`email`/`loginId` and the sign-up screen's `fullName`);
              everything stays editable — accepting with a different account is allowed and
              disclosed on the accept card. */}
          <Descope
            flowId="sign-up-or-in"
            locale={baseLanguage(language)}
            form={info.email || info.displayName ? {
              ...(info.email ? {email: info.email, loginId: info.email} : {}),
              ...(info.displayName ? {fullName: info.displayName, name: info.displayName} : {})
            } : undefined}
          />
        </Card>
      )}
    </EntryShell>
  )
}

function AcceptCard({
  token,
  invitedEmail,
  tenantName,
}: {
  token: string
  invitedEmail?: string
  tenantName: string | null
}) {
  const t = me.useT()
  const navigate = useNavigate()
  const sdk = useDescope()
  const { user } = useUser()
  const sessionEmail = user?.email

  const emailMismatch = Boolean(invitedEmail && sessionEmail && invitedEmail.toLowerCase() !== sessionEmail.toLowerCase())

  const accept = useMutation({
    ...platformManagementAcceptInvitationMutation(),
    onError: () => track('invitation.accepted', { emailMismatch, outcome: 'failed' }),
    onSuccess: async () => {
      track('invitation.accepted', { emailMismatch, outcome: 'ok' })
      // The new tenant membership/roles land in the JWT only after a refresh; do it before /me
      // loads so the overview immediately shows the new organizations.
      await sdk.refresh()
      await navigate({ to: '/me' })
    },
  })

  return (
    <Card className="p-6 space-y-4">
      {emailMismatch && (
        <Alert variant="warning">
          <TriangleAlertIcon />
          <AlertTitle>{t('invite.differentAccountTitle')}</AlertTitle>
          <AlertDescription>
            <me.Trans
              k="invite.differentAccountBody"
              values={{ invited: invitedEmail, current: sessionEmail }}
              components={{ b: <span className="font-medium" /> }}
            />
          </AlertDescription>
        </Alert>
      )}
      {accept.isError && (
        <Alert variant="danger">
          <TriangleAlertIcon />
          <AlertTitle>{t('invite.acceptFailedTitle')}</AlertTitle>
          <AlertDescription>{t('invite.acceptFailedBody')}</AlertDescription>
        </Alert>
      )}
      <p>
        {sessionEmail ? (
          <me.Trans
            k={tenantName !== null ? 'invite.acceptToAs' : 'invite.acceptToSomeTenantAs'}
            values={{ tenant: tenantName ?? undefined, email: sessionEmail }}
            components={{ b: <span className="font-medium" /> }}
          />
        ) : tenantName !== null ? t('invite.acceptTo', { tenant: tenantName }) : t('invite.acceptToSomeTenant')}
      </p>
      <Button variant="primary" icon={UserPlusIcon} loading={accept.isPending} onClick={() => accept.mutate({ body: { token } })}>
        {t('invite.accept')}
      </Button>
    </Card>
  )
}
